import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { martialArts } from "../../../content";
import type { MartialArtsAchievement, MartialArtsDiscipline } from "../../../types/content";

/**
 * ============================================================================
 * Martial Arts & Service — the Home teaser.
 * ============================================================================
 *
 * Three facts and a link to `/martial-arts`.
 *
 * WHICH three is an editorial choice, so it lives HERE and not in
 * `martialArts.json` — the D-065 ruling, applied at the point it was meant for.
 * The content layer records what happened; a component decides what leads. Put
 * a `featured: true` in the JSON instead and the layer starts carrying an
 * opinion about presentation that a future sync would have to preserve.
 *
 * THE FACTS THEMSELVES ARE STILL RESOLVED FROM THE LAYER, BY ID. Nothing below
 * is a literal: the ids name records and the copy comes out of them, so a
 * corrected event name or a corrected placing reaches this strip without anyone
 * remembering it exists. `resolve` throws in development if an id stops
 * matching, because a teaser that silently renders two facts instead of three
 * is precisely the D-065 failure — a section quietly showing less than it holds.
 */

/** Editorial pick, by content-layer id. Owner ruling, 2026-08-20. */
const FEATURED: ReadonlyArray<{ disciplineId: string; achievementId: string; caption: string }> = [
	{
		disciplineId: "karate",
		achievementId: "karate-federation-cup-2018",
		caption: "National champion — Kumite"
	},
	{
		disciplineId: "muay-thai",
		achievementId: "muay-thai-minf-senior-national-2017",
		caption: "National silver — 63.5-67kg"
	},
	{
		disciplineId: "ncc",
		achievementId: "ncc-best-cadet-2016",
		caption: "Sergeant, 2 Bengal Battalion NCC"
	}
];

interface Resolved {
	caption: string;
	event: string;
	year: string;
	/* Carried through so the card can LINK to the discipline it came from
	   (D-100). It was already in `FEATURED`; it simply was not being kept. */
	disciplineId: string;
	disciplineName: string;
}

const resolve = (): Resolved[] =>
	FEATURED.map((pick): Resolved | null => {
		const discipline = martialArts.find((d: MartialArtsDiscipline) => d.id === pick.disciplineId);
		const achievement = discipline?.achievements.find((a: MartialArtsAchievement) => a.id === pick.achievementId);
		/* `!discipline` is now in the guard as well as `!achievement`. It cannot
		   be false while the achievement resolved — the achievement was found
		   THROUGH it — but the compiler cannot see that, and widening the guard
		   is honest where a non-null assertion would only silence it. */
		if (!discipline || !achievement) {
			/* Same guard as the certifications bridge (D-042 form): loud in
			   development, silent in production, and dead-code-eliminated there. */
			if (!import.meta.env.PROD) {
				// eslint-disable-next-line no-console
				console.error(
					`MartialArtsTeaser: no achievement "${pick.achievementId}" in discipline "${pick.disciplineId}".`
				);
			}
			return null;
		}
		return {
			caption: pick.caption,
			event: achievement.event,
			year: achievement.year,
			disciplineId: discipline.id,
			disciplineName: discipline.name
		};
	}).filter((r: Resolved | null): r is Resolved => r !== null);

/**
 * One featured record, and THE WHOLE CARD IS ONE LINK (D-100, owner
 * instruction). It lands on the DISCIPLINE section of `/martial-arts` rather
 * than on the individual placing: the three cards are one placing each, but the
 * page sets them in a table under a heading that carries the grade, the unit
 * and the years, and dropping a reader into a table row would lose all of it.
 *
 * Same stretched-link pattern as every other teaser on this page (D-099): a
 * real link on the heading, `after:inset-0` over the card, one tab stop. The
 * pseudo-element is invisible to the DOM, so the gate hit-tests the card's
 * centre rather than trusting that the class is present.
 */
const TeaserCard = ({ record, index }: { record: Resolved; index: number }): JSX.Element => {
	const reduceMotion = useReducedMotion();

	return (
		<motion.li
			className="card-shell group relative"
			initial={reduceMotion ? false : { opacity: 0, y: 24 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.2 }}
			transition={{ duration: 0.5, delay: Math.min(index * 0.06, 0.24), ease: [0.22, 1, 0.36, 1] }}
		>
			<div className="card-face flex h-full flex-col p-6">
				{/* The discipline, so the card says what record it belongs to before
				    a reader follows it. It is also the link's context: "National
				    champion — Kumite" alone does not say where the link goes. */}
				<span className="pill-tint pill-tint-upper pill-tint-wrap self-start border-[var(--cat-indigo-border)] bg-[var(--cat-indigo-fill)] text-[var(--cat-indigo-text)]">
					{record.disciplineName}
				</span>

				<h3 className="mt-4 text-sm font-bold leading-snug text-[var(--text-strong)]">
					<Link
						to={`/martial-arts#${record.disciplineId}`}
						className="rounded-[4px] transition-colors duration-200 after:absolute after:inset-0 after:rounded-[1rem] after:content-[''] hover:text-[var(--accent-link-hover)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
					>
						{record.caption}
					</Link>
				</h3>

				<p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">{record.event}</p>

				<div className="mt-auto flex items-baseline justify-between gap-3 pt-5">
					<span className="text-sm font-semibold tabular-nums text-[var(--text-muted)]">{record.year}</span>
					{/* aria-hidden — the heading link already names the destination. */}
					<span
						aria-hidden="true"
						className="text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 group-hover:text-[var(--accent-link-hover)]"
					>
						See the record &rarr;
					</span>
				</div>
			</div>
		</motion.li>
	);
};

export default function MartialArtsTeaser(): JSX.Element {
	const featured = resolve();

	return (
		<section className="section-shell" id="martial-arts" aria-labelledby="martial-arts-teaser-heading">
			<div className="section-rail">
				<p className="section-eyebrow">Discipline &amp; Service</p>

				<h2 id="martial-arts-teaser-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Martial Arts
					</span>{" "}
					&amp; Service
				</h2>

				{/* Same rule as the page standfirst: describes what the record is, asserts no
				    span the layer cannot settle, and types no count the stat cards compute. */}
				<p className="section-standfirst">
					Competitive karate and Muay Thai, and service in the National Cadet Corps — grades, placings, camps
					and officiating roles.
				</p>

				<ul className="mt-10 grid list-none auto-rows-fr grid-cols-1 gap-5 p-0 sm:grid-cols-2 lg:grid-cols-3">
					{featured.map((f: Resolved, index: number) => (
						<TeaserCard key={f.event} record={f} index={index} />
					))}
				</ul>

				<div className="mt-10">
					<Link
						to="/martial-arts"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						See the full record →
					</Link>
				</div>
			</div>
		</section>
	);
}
